'use client';

import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type ConfirmationResult,
} from 'firebase/auth';
import { getFirebaseAuth } from './firebase';

export type FirebaseConfirmation = ConfirmationResult;

let recaptchaVerifier: RecaptchaVerifier | null = null;

/**
 * Create (or reuse) an invisible reCAPTCHA verifier attached to `containerId`.
 * Must be called from a browser click handler.
 */
function getRecaptchaVerifier(containerId: string): RecaptchaVerifier {
  if (!recaptchaVerifier) {
    const auth = getFirebaseAuth();
    recaptchaVerifier = new RecaptchaVerifier(auth, containerId, { size: 'invisible' });
  }
  return recaptchaVerifier;
}

/**
 * Send an OTP to `phoneNumber` via Firebase Phone Auth.
 * Returns a ConfirmationResult that must be passed to `confirmFirebaseOtp`.
 *
 * @param phoneNumber E.164 format, e.g. "+919876543210"
 * @param recaptchaContainerId ID of a hidden div in the DOM (e.g. "firebase-recaptcha")
 */
export async function sendFirebaseOtp(
  phoneNumber: string,
  recaptchaContainerId: string,
): Promise<FirebaseConfirmation> {
  const verifier = getRecaptchaVerifier(recaptchaContainerId);
  const auth = getFirebaseAuth();
  return signInWithPhoneNumber(auth, phoneNumber, verifier);
}

/**
 * Confirm the OTP entered by the user.
 * Returns a Firebase ID token (JWT) containing the verified `phone_number` claim.
 */
export async function confirmFirebaseOtp(
  confirmation: FirebaseConfirmation,
  code: string,
): Promise<string> {
  const result = await confirmation.confirm(code);
  return result.user.getIdToken();
}

/** Reset the shared reCAPTCHA verifier (call if you need a fresh one after an error). */
export function resetRecaptchaVerifier() {
  if (recaptchaVerifier) {
    recaptchaVerifier.clear();
    recaptchaVerifier = null;
  }
}
